package ru.chm.gradereport.ui.screens.publiccheckin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import ru.chm.gradereport.data.GradeApi
import ru.chm.gradereport.model.PublicCheckInInfoDto
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.ErrorBanner
import ru.chm.gradereport.ui.components.ErrorStateCard
import ru.chm.gradereport.ui.components.LoadingBox
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.PrimaryButton
import ru.chm.gradereport.ui.components.ScreenHeader
import ru.chm.gradereport.ui.components.ScreenScaffold
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.GreenAccent

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PublicCheckInScreen(
    api: GradeApi,
    token: String,
) {
    var info by remember { mutableStateOf<PublicCheckInInfoDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var nameQuery by remember { mutableStateOf("") }
    var menuExpanded by remember { mutableStateOf(false) }
    var selectedId by remember { mutableStateOf<String?>(null) }
    var submitting by remember { mutableStateOf(false) }
    var successName by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val colors = GradeTheme.colors

    LaunchedEffect(token) {
        loading = true
        error = null
        successName = null
        selectedId = null
        try {
            info = api.publicCheckInInfo(token)
        } catch (e: Exception) {
            error = e.message ?: "QR-код недоступен"
            info = null
        } finally {
            loading = false
        }
    }

    val query = nameQuery.trim()
    val students = remember(info, query) {
        info?.students
            ?.filter { query.isEmpty() || it.fullName.contains(query, ignoreCase = true) }
            .orEmpty()
    }

    ScreenScaffold {
        ScreenHeader(
            title = info?.courseName ?: "Отметиться",
            subtitle = when {
                successName != null -> "Отметка принята"
                info?.expired == true -> "Срок действия QR истёк"
                info != null -> info!!.sessionLabel
                error != null -> "Не удалось открыть форму"
                else -> "Самоотметка по QR"
            },
        )

        when {
            loading -> LoadingBox()
            successName != null -> AppCard(modifier = Modifier.fillMaxWidth().padding(top = 4.dp)) {
                Text(
                    "Готово",
                    color = GreenAccent,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                )
                Text(
                    "$successName — отметка отправлена. Дождитесь подтверждения преподавателя.",
                    color = colors.textPrimary,
                    modifier = Modifier.padding(top = 8.dp),
                    lineHeight = 22.sp,
                )
            }
            info == null -> ErrorStateCard(
                title = "QR недоступен",
                message = error ?: "Код не найден или уже погашен",
                hint = "Попросите создать новый QR.",
                modifier = Modifier.padding(top = 4.dp),
            )
            info!!.expired -> ErrorStateCard(
                title = "Время вышло",
                message = "Срок действия этого QR-кода истёк.",
                hint = "Попросите новый код у преподавателя.",
                modifier = Modifier.padding(top = 4.dp),
            )
            else -> {
                ErrorBanner(error) { error = null }
                MutedText(
                    "Начните вводить ФИО и выберите себя в списке.",
                    modifier = Modifier.padding(top = 4.dp, bottom = 8.dp),
                )
                ExposedDropdownMenuBox(
                    expanded = menuExpanded,
                    onExpandedChange = { menuExpanded = it },
                ) {
                    AppTextField(
                        value = nameQuery,
                        onValueChange = { value ->
                            nameQuery = value
                            // Любое изменение текста снова открывает список,
                            // чтобы можно было выбрать другой вариант
                            menuExpanded = true
                            // Выбор сбрасывается, если текст больше не совпадает с выбранным студентом
                            selectedId = selectedId?.takeIf { id ->
                                info?.students
                                    ?.firstOrNull { it.id == id }
                                    ?.fullName
                                    ?.equals(value.trim(), ignoreCase = true) == true
                            }
                        },
                        label = "Ваше ФИО",
                        trailingIcon = {
                            DropdownChevron(
                                expanded = menuExpanded,
                                tint = colors.textMuted,
                            )
                        },
                        modifier = Modifier
                            .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryEditable)
                            .onFocusChanged { if (it.isFocused) menuExpanded = true },
                    )
                    ExposedDropdownMenu(
                        expanded = menuExpanded,
                        onDismissRequest = { menuExpanded = false },
                        shape = RoundedCornerShape(12.dp),
                        containerColor = colors.card,
                        border = BorderStroke(1.dp, colors.border),
                        modifier = Modifier.exposedDropdownSize(),
                    ) {
                        if (students.isEmpty()) {
                            DropdownMenuItem(
                                text = { Text("Никого не найдено", color = colors.textMuted) },
                                enabled = false,
                                onClick = {},
                            )
                        } else {
                            students.forEach { student ->
                                DropdownMenuItem(
                                    text = {
                                        Text(
                                            student.fullName,
                                            color = if (student.id == selectedId) {
                                                GreenAccent
                                            } else {
                                                colors.textPrimary
                                            },
                                            fontWeight = if (student.id == selectedId) {
                                                FontWeight.SemiBold
                                            } else {
                                                FontWeight.Normal
                                            },
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis,
                                        )
                                    },
                                    onClick = {
                                        selectedId = student.id
                                        nameQuery = student.fullName
                                        menuExpanded = false
                                    },
                                )
                            }
                        }
                    }
                }
                PrimaryButton(
                    text = if (submitting) "Отправка…" else "Отметиться",
                    enabled = !submitting && selectedId != null,
                    modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                    onClick = {
                        val id = selectedId ?: return@PrimaryButton
                        val name = info?.students?.firstOrNull { it.id == id }?.fullName.orEmpty()
                        scope.launch {
                            submitting = true
                            error = null
                            try {
                                api.submitPublicCheckIn(token, id)
                                successName = name
                            } catch (e: Exception) {
                                error = e.message
                            } finally {
                                submitting = false
                            }
                        }
                    },
                )
            }
        }
    }
}

/** Chevron drawn via Canvas — the "▾" glyph is missing in the web font. */
@Composable
private fun DropdownChevron(
    expanded: Boolean,
    tint: Color,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier.size(24.dp),
        contentAlignment = Alignment.Center,
    ) {
        Canvas(modifier = Modifier.size(18.dp)) {
            val stroke = 2.5.dp.toPx()
            val w = size.width
            val h = size.height
            val midX = w / 2f
            if (expanded) {
                drawLine(
                    color = tint,
                    start = Offset(w * 0.2f, h * 0.62f),
                    end = Offset(midX, h * 0.32f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
                drawLine(
                    color = tint,
                    start = Offset(midX, h * 0.32f),
                    end = Offset(w * 0.8f, h * 0.62f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
            } else {
                drawLine(
                    color = tint,
                    start = Offset(w * 0.2f, h * 0.38f),
                    end = Offset(midX, h * 0.68f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
                drawLine(
                    color = tint,
                    start = Offset(midX, h * 0.68f),
                    end = Offset(w * 0.8f, h * 0.38f),
                    strokeWidth = stroke,
                    cap = StrokeCap.Round,
                )
            }
        }
    }
}
